import * as Sentry from "@sentry/browser";

if (window.sentry_dsn) {
    /**
     * Runs a callback shortly after the visitor first interacts with the page.
     *
     * @param {() => void} callback - The work to defer off the interaction critical path.
     * @returns {void}
     */
    function afterFirstInteraction(callback) {
        const events = ["pointerdown", "keydown"];

        const schedule = () => {
            events.forEach((event) =>
                window.removeEventListener(event, schedule),
            );

            window.setTimeout(callback, 500);
        };

        events.forEach((event) =>
            window.addEventListener(event, schedule, { passive: true }),
        );
    }

    Sentry.init({
        dsn: window.sentry_dsn,
        release: window.app_release,
        dataCollection: {
            userInfo: true,
        },
        integrations: [
            // Resource timings and calls to third parties were most of the browser span quota and nothing we act on.
            // Requests to our own sites stay, they carry the trace to the backend.
            Sentry.browserTracingIntegration({
                ignoreResourceSpans: [
                    "resource.script",
                    "resource.img",
                    "resource.css",
                    "resource.link",
                    "resource.other",
                    "resource.beacon",
                    "resource.iframe",
                    "resource.video",
                    "resource.icon",
                ],
                // Sentry passes the URL as given to fetch/XHR, so relative calls like /api/... must be resolved first
                shouldCreateSpanForRequest: (url) =>
                    /^https:\/\/www\.luxplus\.(be|co\.uk|dk|fi|nl|no|se)\//.test(
                        new URL(url, window.location.href).href,
                    ),
            }),
            // Sentry wraps XHR callbacks and event listeners to add metadata. The
            // side effect is that a vendor's own bug is caught inside our wrapper
            // and reported with a @sentry/browser frame on top, so it reads as
            // first-party code. Since allowUrls below already excludes vendor
            // frames by file, a re-enabled wrapper frame (which lives in our own
            // bundle) would let that same vendor noise back in.
            Sentry.browserApiErrorsIntegration({
                XMLHttpRequest: false,
                eventTarget: false,
            }),
        ],
        tracesSampleRate: 0.001,
        tracePropagationTargets: [
            /^https:\/\/www\.luxplus\.(be|co\.uk|dk|fi|nl|no|se)\//,
        ],
        profilesSampleRate: 0.0,
        replaysSessionSampleRate: 0.0,
        replaysOnErrorSampleRate: 0.01,
        // All first-party JS now lives in our Vite bundle, Livewire/Alpine
        // included. Matching on that instead of denying known vendor files
        // means a vendor's inline snippet throwing on the page URL — a
        // denyUrls pattern can never catch that, since the throwing frame is
        // the page, not the vendor's file — is excluded too, without needing
        // a pattern per vendor.
        allowUrls: [/\/build\/assets\//],
        ignoreErrors: [
            "Non-Error promise rejection captured.+",
            // WKWebView / Android WebView bridges injected by the native app.
            // Not referenced anywhere in our source.
            /window\.webkit\.messageHandlers/,
            "Error invoking postMessage: Java object is gone",
        ],
    });

    afterFirstInteraction(() => {
        Sentry.getClient()?.addIntegration(Sentry.replayIntegration());
    });
}
